import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const { name, email, subject, message } = formData;
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = "Name is required*";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required*";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address*";
    }
    if (!subject.trim()) {
      newErrors.subject = "Subject is required*";
    }
    if (!message.trim()) {
      newErrors.message = "Message is required*";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name.trim(),
            email: formData.email.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim(),
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Failed to send your message.");
      }

      toast.success(
        data.message || "Thanks for reaching out! We'll get back to you soon."
      );
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(
        error.message || "Something went wrong. Please try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="px-8 lg:px-32 xl:px-52">
        <h2 className="text-center text-2xl lg:text-3xl font-bold mb-2 mt-10">
          Contact Us
        </h2>
        <p className="text-center text-gray-600 text-sm md:text-base mb-2">
          Have a question or feedback about NutriLens AI? We'd love to hear from
          you.
        </p>
        <div className="container mx-auto px-5 py-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:grid-cols-2">
              <div>
                <label
                  className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                  htmlFor="name"
                >
                  Name*
                </label>
                <input
                  className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none focus:border-[#12AD63] placeholder-small"
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter Your Name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm text-left">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label
                  className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                  htmlFor="email"
                >
                  Email*
                </label>
                <input
                  className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none focus:border-[#12AD63] placeholder-small"
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter Your Email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm text-left">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                htmlFor="subject"
              >
                Subject*
              </label>
              <input
                className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none focus:border-[#12AD63] placeholder-small"
                id="subject"
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Enter Subject"
              />
              {errors.subject && (
                <p className="text-red-500 text-sm text-left">
                  {errors.subject}
                </p>
              )}
            </div>
            <div>
              <label
                className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                htmlFor="message"
              >
                Message*
              </label>
              <textarea
                className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none focus:border-[#12AD63] h-32"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your message"
              />
              {errors.message && (
                <p className="text-red-500 text-sm text-left">
                  {errors.message}
                </p>
              )}
            </div>
            <div className="flex justify-center mt-4">
              <button
                className="text-white bg-[#12AD63] focus:outline-none hover:bg-[#0d8f4f] font-medium rounded-lg text-[15px] px-10 py-2.5 text-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit"}
              </button>
            </div>

            <div className="flex flex-col items-center mt-4">
              <p className="text-sm text-gray-600 mb-2">
                You can also reach us directly at
                <a
                  href="mailto:support@sstechstudio.com"
                  className="text-[#12AD63] underline ml-1"
                >
                  support@sstechstudio.com
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Contact;
